export interface DocumentReadinessNoticeData {
  message: string;
}

export interface DocumentReadinessDocument {
  document_id?: string;
  id?: string;
  name?: string;
  filename?: string;
  state?: string | null;
}

const MAX_DISPLAYED_DOCUMENTS = 4;
const DOCUMENTS_NOT_READY_NEXT_STEP = "Please wait for processing to finish or retry failed documents.";

const pluralizeDocument = (count: number) => (count === 1 ? "document" : "documents");

const getReadinessDocumentLabel = (document: DocumentReadinessDocument, fallbackStatus: string) => {
  const name = document.name || document.filename || document.document_id || document.id || "Untitled document";
  const statusLabel = String(document.state || fallbackStatus)
    .toLowerCase()
    .replace(/_/g, " ");

  return `${name} (${statusLabel})`;
};

const buildDocumentsNotReadyMessage = (
  notReady: DocumentReadinessDocument[],
  failed: DocumentReadinessDocument[]
) => {
  const total = notReady.length + failed.length;

  if (total <= MAX_DISPLAYED_DOCUMENTS) {
    const documents = [
      ...notReady.map((document) => getReadinessDocumentLabel(document, "not ready")),
      ...failed.map((document) => getReadinessDocumentLabel(document, "failed")),
    ];

    return `The request cannot be completed because these documents are not ready: ${documents.join(
      ", "
    )}. ${DOCUMENTS_NOT_READY_NEXT_STEP}`;
  }

  const blockedDocuments: string[] = [];

  if (notReady.length) {
    blockedDocuments.push(`${notReady.length} still processing`);
  }

  if (failed.length) {
    blockedDocuments.push(`${failed.length} failed`);
  }

  const blockedDocumentsSummary = blockedDocuments.length ? ` (${blockedDocuments.join(", ")})` : "";

  return `The request cannot be completed because ${total} ${pluralizeDocument(
    total
  )} are not ready${blockedDocumentsSummary}. ${DOCUMENTS_NOT_READY_NEXT_STEP}`;
};

export const getDocumentsNotReadyPayload = (error: any): DocumentReadinessNoticeData | null => {
  const data = error?.response?.data;

  if (data?.error !== "documents_not_ready") {
    return null;
  }

  return {
    message:
      data?.message ||
      "The request cannot be completed because project documents are not ready. Please wait for processing to finish or retry failed documents.",
  };
};

export const getDocumentsNotReadyNoticeFromDocuments = (
  documents: DocumentReadinessDocument[]
): DocumentReadinessNoticeData | null => {
  const notReady: DocumentReadinessDocument[] = [];
  const failed: DocumentReadinessDocument[] = [];

  documents.forEach((document) => {
    const normalizedState = String(document?.state || "").toUpperCase();

    if (normalizedState === "COMPLETED") {
      return;
    }

    if (normalizedState === "FAILED") {
      failed.push(document);
      return;
    }

    notReady.push(document);
  });

  if (!notReady.length && !failed.length) {
    return null;
  }

  return {
    message: buildDocumentsNotReadyMessage(notReady, failed),
  };
};

export const isDocumentsNotReadyError = (error: any) => Boolean(getDocumentsNotReadyPayload(error));
